import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { FileWithPath, useDropzone } from "react-dropzone";
import { Image } from "../../../../models/product_model";
import { GET_PRODUCT, PRODUCT_IMAGE_UPLOAD } from "../queries";

type DropzoneProps = {
  cancel: () => void;
  productId: number;
};

type FileWithPreview = FileWithPath & {
  preview: string;
};

const Basic: React.FC<DropzoneProps> = ({ cancel, productId }) => {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [mutate, { called, error, loading }] = useMutation(
    PRODUCT_IMAGE_UPLOAD,
    {
      refetchQueries: [{ query: GET_PRODUCT, variables: { id: productId } }],
    }
  );

  useEffect(() => {
    if (called && !error) {
      cancel();
    }
    if (error) {
      console.error("Error uploading image:", error);
      alert("Error al subir la imagen. Por favor intenta de nuevo.");
    }
  }, [called, error, cancel]);

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    disabled: loading,
    maxSize: 5 * 1024 * 1024, // 5MB limit
    accept: "image/png,image/jpeg",
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const rejectedFile = rejectedFiles[0];
        if (
          rejectedFile.errors.some((error) => error.code === "file-too-large")
        ) {
          alert("El archivo es demasiado grande. El tamaño máximo es 5MB.");
        } else if (
          rejectedFile.errors.some(
            (error) => error.code === "file-invalid-type"
          )
        ) {
          alert("Solo se permiten archivos PNG y JPG.");
        } else {
          alert(
            "Error al procesar el archivo. Por favor intenta con otro archivo."
          );
        }
        return;
      }

      // Additional client-side validation for file types
      const validTypes = ["image/png", "image/jpeg", "image/jpg"];
      const invalidFiles = acceptedFiles.filter(
        (file) => !validTypes.includes(file.type)
      );

      if (invalidFiles.length > 0) {
        alert("Solo se permiten archivos PNG y JPG.");
        return;
      }

      const filesWithPreview = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );
      setFiles(filesWithPreview);

      // Auto-upload when file is selected
      if (filesWithPreview.length > 0) {
        mutate({ variables: { file: filesWithPreview[0], productId } });
      }
    },
  });

  const thumbs = files.map((file: FileWithPreview) => (
    <div className="thumb" key={file.name}>
      <div className="thumbInner">
        <img src={file.preview} alt="thumbnail" className="thumbImg" />
        {loading && (
          <div className="upload-overlay">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Subiendo...</p>
          </div>
        )}
      </div>
    </div>
  ));

  return (
    <section className="container">
      {files.length === 0 && (
        <div {...getRootProps({ className: "dropzone" })}>
          <input {...getInputProps()} />
          <p>
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Subiendo imagen...
              </>
            ) : (
              "Arrastra una imagen PNG o JPG o haz click para seleccionarla"
            )}
          </p>
        </div>
      )}
      <aside className="thumbsContainer">{thumbs}</aside>
      <div className="row pt-1">
        <button
          className="btn btn-secondary btn-sm"
          onClick={cancel}
          disabled={loading}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin"></i> Cancelar
            </>
          ) : (
            "Cancelar"
          )}
        </button>
      </div>
    </section>
  );
};

type Props = {
  productId: number;
  images?: Image[];
};

const ProductImage: React.FC<Props> = ({ images, productId }) => {
  const [edit, setEdit] = useState(false);

  return (
    <>
      {edit && (
        <div>
          <Basic productId={productId} cancel={() => setEdit(false)} />
        </div>
      )}
      {!edit && (
        <div className="product_img d-inline-block">
          <img
            src={
              images && images.length > 0
                ? images[0].url
                    .replace("ftp-foo.s3.amazonaws.com", "cdn.foopos.mx")
                    .replace("s3.amazonaws.com/ftp-foo", "cdn.foopos.mx")
                : "https://s3.amazonaws.com/ftp-foo/page_assets/no_photo_04.jpg"
            }
            className="img-thumbnail"
            alt="stuff"
          />
          {productId > 0 && (
            <div className="overlay_options d-flex align-items-center justify-content-center">
              <button
                className="btn btn-link "
                type="button"
                onClick={() => setEdit(true)}
              >
                <i className="fas fa-edit" aria-hidden="true"></i>
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ProductImage;
